// Erzeugt eine iCalendar-Datei aus den Stimmabgaben eines Voters.
// Pro YES- und MAYBE-Slot ein VEVENT, importierbar in Apple/Google/Outlook.
//
// YES → STATUS:CONFIRMED → fest im Kalender
// MAYBE → STATUS:TENTATIVE → meist mit anderem Stil dargestellt
// NO → wird ignoriert
//
// Zeitzone: floating local time (keine TZID) — Importierender Kalender
// nimmt seine lokale Zeit. Für „Slot reservieren mir die Wahl-Termine"
// ist das richtig.

import type { Appointment } from './types';

export type ReservationInput = {
  surveyTitle: string | null;
  slots: Appointment[];
  responses: Record<string, 'yes' | 'maybe' | 'no'>;
  /** Eindeutiger UID-Präfix — nutzen wir den Voter-Token, damit Updates
   *  beim erneuten Import dieselben Events ersetzen statt zu duplizieren. */
  voterToken: string;
  /** Optionaler Survey-Ort (gilt für ALLE Slots). */
  surveyLocation?: string | null;
};

export function buildReservationIcs(input: ReservationInput): string {
  const titlePrefix = input.surveyTitle?.trim() || 'Slot';
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//slot.gotyou.at//Slot Reservation//DE',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];

  const stamp = formatStamp(new Date());

  for (const slot of input.slots) {
    const choice = input.responses[slot.id];
    if (choice !== 'yes' && choice !== 'maybe') continue;

    const status = choice === 'yes' ? 'CONFIRMED' : 'TENTATIVE';
    const symbol = choice === 'yes' ? '✓' : '?';

    const event: string[] = [
      'BEGIN:VEVENT',
      `UID:slot-${input.voterToken}-${slot.id}@slot.gotyou.at`,
      `DTSTAMP:${stamp}`,
      `DTSTART:${formatLocal(slot.date, slot.startMinutes)}`,
      `DTEND:${formatLocal(slot.date, slot.endMinutes)}`,
      `SUMMARY:${escape(`[Slot ${symbol}] ${titlePrefix}`)}`,
      `STATUS:${status}`,
      'CATEGORIES:Slot',
    ];
    if (input.surveyLocation) event.push(`LOCATION:${escape(input.surveyLocation)}`);
    event.push(
      `DESCRIPTION:${escape('Reservierter Termin aus Slot. Solange noch nicht klar ist, welcher der Wahl-Slots final wird, blockst du sie alle. Nach der finalen Festlegung kannst du die anderen entfernen.')}`,
      'END:VEVENT',
    );
    lines.push(...event);
  }

  lines.push('END:VCALENDAR');
  return lines.join('\r\n') + '\r\n';
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatStamp(d: Date): string {
  // UTC-Format „20260502T103000Z"
  return (
    String(d.getUTCFullYear()) +
    pad2(d.getUTCMonth() + 1) +
    pad2(d.getUTCDate()) +
    'T' +
    pad2(d.getUTCHours()) +
    pad2(d.getUTCMinutes()) +
    pad2(d.getUTCSeconds()) +
    'Z'
  );
}

function formatLocal(date: string, minutes: number): string {
  // Floating local time: „20260502T093000" (kein Z, keine TZID).
  return (
    date.replace(/-/g, '') +
    'T' +
    pad2(Math.floor(minutes / 60)) +
    pad2(minutes % 60) +
    '00'
  );
}

function escape(s: string): string {
  return s
    .replace(/\\/g, '\\\\')
    .replace(/\r?\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

export function downloadReservationIcs(input: ReservationInput): void {
  const text = buildReservationIcs(input);
  const blob = new Blob([text], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safeName = (input.surveyTitle?.trim() || 'slot')
    .replace(/[^A-Za-z0-9äöüÄÖÜß \-_]/g, '')
    .substring(0, 40)
    .trim() || 'slot';
  a.href = url;
  a.download = `slot-reservation-${safeName}.ics`;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
